import BreadcrumbTitle from "components/BreadcrumbTitle"
import { SaveButton } from "components/Buttons"
import PageMetaTags from "components/PageMetaTags"
import {
  CheckboxField,
  SingleSelectField,
  TextField,
} from "components/RenderInput"
import { FORM_NAMES, URL_PATHS } from "constants/index"
import React, { useEffect } from "react"
import { connect } from "react-redux"
import { Card, CardBody, Col, Container, Row } from "reactstrap"
import { Field, initialize, reduxForm } from "redux-form"
import {
  createMetaTag,
  getBusinessUnits,
  getTag,
  resetTag,
  updateMetaTag,
} from "store/actions"
import { userIsCorporate, userIsGodMode } from "utils"

const required = value => (value ? undefined : "Tag required")

const TagsForm = ({
  match,
  selectedTag,
  getTag,
  resetTag,
  history,
  businessUnits,
  getBusinessUnits,
  initializeForm,
  handleSubmit,
  vendorId,
  createMetaTag,
  updateMetaTag,
}) => {
  useEffect(() => {
    if (match?.params?.id) {
      getTag({ id: match?.params?.id })
    }
    if (userIsCorporate() || userIsGodMode()) {
      getBusinessUnits()
    }
    return () => {
      resetTag()
    }
  }, [])

  useEffect(() => {
    if (!!selectedTag) {
      initializeForm({
        tagName: selectedTag?.tag,
        businessUnit: selectedTag?.business_unit_id,
        limitLandingScreen: selectedTag?.limit_to_landing_screen,
        hideUserList: selectedTag?.hide_users_list,
        forceHiddenSP: selectedTag?.do_not_allow_to_have_public_SP,
        disableDP: selectedTag?.do_not_allow_to_use_DP,
        disableExpNotifications: selectedTag?.disable_experience_notifications,
      })
    }
  }, [selectedTag])

  const handleSaveTag = data => {
    if (!!selectedTag) {
      updateMetaTag({
        id: selectedTag?.id,
        values: {
          ...selectedTag,
          business_unit_id: data?.businessUnit,
          invisible: data?.tagName?.substring(0, 1) === "@",
          tag: data?.tagName,
          limit_to_landing_screen: data?.limitLandingScreen ? 1 : 0,
          hide_users_list: data?.hideUserList ? 1 : 0,
          do_not_allow_to_have_public_SP: data?.forceHiddenSP ? 1 : 0,
          do_not_allow_to_use_DP: data?.disableDP ? 1 : 0,
          disable_experience_notifications: data?.disableExpNotifications
            ? 1
            : 0,
        },
        callback: () => {
          history?.push(URL_PATHS.TAGS)
        },
      })
    } else {
      createMetaTag({
        values: {
          business_unit_id: data?.businessUnit,
          invisible: data?.tagName?.substring(0, 1) === "@",
          tag: data?.tagName,
          vendor_id: vendorId,
          limit_to_landing_screen: data?.limitLandingScreen ? 1 : 0,
          hide_users_list: data?.hideUserList ? 1 : 0,
          do_not_allow_to_have_public_SP: data?.forceHiddenSP ? 1 : 0,
          do_not_allow_to_use_DP: data?.disableDP ? 1 : 0,
          disable_experience_notifications: data?.disableExpNotifications
            ? 1
            : 0,
        },
        callback: () => {
          history?.push(URL_PATHS.TAGS)
        },
      })
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <PageMetaTags title={match?.params?.id ? "Edit Tag" : "Create Tag"} />
        <Container fluid>
          <BreadcrumbTitle
            title={match?.params?.id ? "Edit Tag" : "Create Tag"}
            breadcrumbItems={[
              { title: "Dashboard" },
              {
                title: "Tag Management",
                link: "/tags",
              },
              {
                title: match?.params?.id ? "Edit Tag" : "Create Tag",
              },
            ]}
          />
          <Row className="d-flex justify-content-end mb-3">
            <Col xs="auto">
              <SaveButton
                type="button"
                color="light"
                content="Back"
                onClick={() => history.goBack()}
              />
            </Col>
          </Row>
          <Card>
            <CardBody>
              <form>
                <Row>
                  <Col>
                    <Field
                      name="tagName"
                      component={TextField}
                      label="Tag"
                      validate={[required]}
                    />
                  </Col>
                </Row>
                {(userIsCorporate() || userIsGodMode()) &&
                  businessUnits?.length > 0 && (
                    <Row className="my-3">
                      <Col>
                        <Field
                          name="businessUnit"
                          label="Business Unit"
                          component={SingleSelectField}
                          options={[
                            {
                              value: null,
                              label: "No Business Unit",
                            },
                            ...businessUnits?.map(bu => ({
                              value: bu?.id,
                              label: bu?.name,
                            })),
                          ]}
                        />
                      </Col>
                    </Row>
                  )}
                <Row className="my-2">
                  <Col>
                    <Field
                      name="limitLandingScreen"
                      label="Limit users to Landing Screen (users which have this tag assigned will be able to log into the app, but will only be able to access the landing screen)"
                      component={CheckboxField}
                    />
                  </Col>
                </Row>
                <Row className="my-2">
                  <Col>
                    <Field
                      name="hideUserList"
                      label="Hide “User List” in mobile apps - bottom tray (users with this tag assigned will not see the user list in the apps)"
                      component={CheckboxField}
                    />
                  </Col>
                </Row>
                <Row className="my-2">
                  <Col>
                    <Field
                      name="forceHiddenSP"
                      label="Force users to have social profile hidden (users which have this tag assigned will always have the social profile hidden and won’t be able to change it)."
                      component={CheckboxField}
                    />
                  </Col>
                </Row>
                <Row className="my-2">
                  <Col>
                    <Field
                      name="disableDP"
                      label="Do not allow users to log in to the DP (users which have that tag assigned will not be able to log into the desktop version)"
                      component={CheckboxField}
                    />
                  </Col>
                </Row>
                <Row className="my-2">
                  <Col>
                    <Field
                      name="disableExpNotifications"
                      label="Do not send experience related notifications (users with this tag assigned will not receive any experience related notifications)"
                      component={CheckboxField}
                    />
                  </Col>
                </Row>
                <Row className="d-flex justify-content-center mt-4">
                  <Col xs="auto">
                    <SaveButton
                      type="button"
                      content="Cancel"
                      color="light"
                      onClick={() => history.push(URL_PATHS.TAGS)}
                    />
                  </Col>
                  <Col xs="auto">
                    <SaveButton
                      type="button"
                      content="Save"
                      onClick={handleSubmit(handleSaveTag)}
                    />
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  selectedTag: state.Tags.selectedTag,
  businessUnits: state.businessUnits.businessUnits,
  vendorId: state.Login.vendorId,
})

const mapDispatchToProps = dispatch => ({
  getTag: values => dispatch(getTag(values)),
  resetTag: () => dispatch(resetTag()),
  getBusinessUnits: values => dispatch(getBusinessUnits(values)),
  initializeForm: values => dispatch(initialize(FORM_NAMES.TAG_FORM, values)),
  createMetaTag: values => dispatch(createMetaTag(values)),
  updateMetaTag: values => dispatch(updateMetaTag(values)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(reduxForm({ form: FORM_NAMES.TAG_FORM })(TagsForm))
